'use client'

import { m } from 'framer-motion'
import Image from 'next/image'

interface CaseGalleryProps {
  images?: string[] | null
  eyebrow?: string
  title?: string
  description?: string
  /** Case/project name, used to build a unique, descriptive alt text per image. */
  caseName?: string
}

export default function CaseGallery({
  images = [],
  eyebrow = 'Галерея',
  title = 'Визуальная часть проекта',
  description = 'Ключевые экраны и интерфейсы, которые показывают архитектуру и качество реализации.',
  caseName,
}: CaseGalleryProps) {
  const gallery =
    Array.isArray(images) && images.length > 0
      ? images
      : ['/cases/case-1.jpg', '/cases/case-2.jpg', '/cases/case-3.jpg', '/cases/case-4.jpg']

  const namePrefix = caseName ? `Кейс «${caseName}»` : 'Кейс из портфолио'
  const galleryAlt = [
    `${namePrefix} — ключевой экран интерфейса`,
    `${namePrefix} — деталь интерфейса крупным планом`,
    `${namePrefix} — промежуточный этап реализации`,
    `${namePrefix} — итоговый экран проекта`,
  ]

  return (
    <section className="home-section relative overflow-hidden bg-transparent">
      <div className="home-container">
        <div className="mb-10 max-w-4xl sm:mb-14">
          <m.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="home-meta mb-4 text-gray-400"
          >
            {eyebrow}
          </m.p>

          <m.h2
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.06 }}
            className="home-title mb-5 text-white"
          >
            {title}
          </m.h2>

          <m.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="home-subnote max-w-3xl text-gray-400"
          >
            {description}
          </m.p>
        </div>

        <div className="grid auto-rows-[220px] grid-cols-1 gap-4 sm:auto-rows-[320px] sm:grid-cols-2 sm:gap-6 lg:auto-rows-[400px] lg:grid-cols-3 lg:gap-8">
          {gallery[0] && (
            <m.div
              initial={{ opacity: 0, scale: 0.97 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45 }}
              className="group relative row-span-1 overflow-hidden rounded-2xl border border-white/10 bg-white/5 sm:col-span-2 sm:rounded-3xl lg:col-span-2"
            >
              <Image
                src={gallery[0]}
                alt={galleryAlt[0]}
                fill
                sizes="(min-width: 1024px) 66vw, 100vw"
                className="object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-transparent" />
            </m.div>
          )}

          {gallery[1] && (
            <m.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: 0.08 }}
              className="group relative col-span-1 row-span-1 overflow-hidden rounded-2xl border border-white/10 bg-white/5 sm:rounded-3xl"
            >
              <Image
                src={gallery[1]}
                alt={galleryAlt[1]}
                fill
                sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
                className="object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
            </m.div>
          )}

          {gallery[2] && (
            <m.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: 0.12 }}
              className="group relative col-span-1 row-span-1 overflow-hidden rounded-2xl border border-white/10 bg-white/5 sm:rounded-3xl"
            >
              <Image
                src={gallery[2]}
                alt={galleryAlt[2]}
                fill
                sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
                className="object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
            </m.div>
          )}

          {gallery[3] && (
            <m.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: 0.16 }}
              className="group relative row-span-1 overflow-hidden rounded-2xl border border-white/10 bg-white/5 sm:col-span-2 sm:rounded-3xl lg:col-span-2"
            >
              <Image
                src={gallery[3]}
                alt={galleryAlt[3]}
                fill
                sizes="(min-width: 1024px) 66vw, 100vw"
                className="object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-transparent" />
            </m.div>
          )}
        </div>
      </div>
    </section>
  )
}
